import { Directive, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { ILogger } from '@nimbus-library/features/logger/services/logger/i-logger';
import {
  LicenseAddonItemDisableReason,
  LicenseItemDisableReason,
  UserLicenseDisabledReasonType,
} from '@nimbus-library/features/shared/components/material/input-license/input-license.model';
import { buildLicenseDisabledReasonsTooltip } from '@nimbus-library/features/shared/components/material/input-license/license-disabled-reasons-tooltip.util';

@Directive()
export abstract class BaseLicenseCheckboxItemComponent implements OnChanges {
  @Input() public selectedItemsCount: number = 1;
  @Input() public itemsWithLicenseCount: number = 1;
  @Input() public availableNumberOfLicenses: number;
  @Input() public disabledReasons: (LicenseItemDisableReason | LicenseAddonItemDisableReason | UserLicenseDisabledReasonType)[] = [];
  @Input() public isSelected: boolean = false;

  @Output()
  public availableNumberOfLicensesChange: EventEmitter<number> = new EventEmitter<number>();

  private get selectedCount(): number {
    return this.selectedItemsCount ?? 1;
  }

  private currentAvailableNumberOfLicenses: number = null;

  private dirty: boolean = false;

  protected disabledReasonsList: string;

  public get isDisabledByLicensesReasons(): boolean {
    const hasNonLicenseBreachDisabledReason = !!this.disabledReasons?.find(
      (x) => x !== LicenseAddonItemDisableReason.PREVENT_LICENSE_BREACH && x !== LicenseItemDisableReason.PREVENT_LICENSE_BREACH,
    );
    const disabledByLicenseBreach =
      !this.isSelected && this.currentAvailableNumberOfLicenses !== null && this.currentAvailableNumberOfLicenses < this.selectedCount;
    return hasNonLicenseBreachDisabledReason || disabledByLicenseBreach;
  }

  public readonly TranslationKeys = TranslationKeys;

  protected abstract readonly disabledReasonsMessage: string;
  protected abstract readonly translationMapper:
    | Record<LicenseItemDisableReason, TranslationKeys>
    | Record<LicenseAddonItemDisableReason, TranslationKeys>
    | Record<UserLicenseDisabledReasonType, TranslationKeys>;

  protected constructor(
    protected readonly translateService: TranslateService,
    protected readonly loggerService: ILogger,
  ) {}

  public ngOnChanges(e: SimpleChanges): void {
    if (e?.availableNumberOfLicenses?.firstChange) {
      this.currentAvailableNumberOfLicenses = e.availableNumberOfLicenses.currentValue;
    }

    if (e.selectedItemsCount || e.itemsWithLicenseCount || e.availableNumberOfLicenses || e.disabledReasons) {
      this.updateDisabledReasons();
    }
  }

  protected updateDisabledReasons(): void {
    if (this.currentAvailableNumberOfLicenses !== null) {
      this.updateLicenseBreachReason();
    }

    this.updateDisabledReasonsList();
  }

  protected recalculateLicensesOnClick(): void {
    if (this.currentAvailableNumberOfLicenses !== null) {
      if (this.isSelected) {
        // 'wasSelected', now is unchecking
        const itemsWithLicenseCount = this.dirty ? this.selectedCount : (this.itemsWithLicenseCount ?? 1);
        this.currentAvailableNumberOfLicenses += itemsWithLicenseCount;
      } else {
        this.currentAvailableNumberOfLicenses -= this.selectedCount;
      }
    }

    this.availableNumberOfLicensesChange.emit(this.currentAvailableNumberOfLicenses);
    this.updateDisabledReasons();

    this.dirty = true;
  }

  private updateLicenseBreachReason(): void {
    if (
      this.currentAvailableNumberOfLicenses < this.selectedCount &&
      !this.disabledReasons.includes(LicenseItemDisableReason.PREVENT_LICENSE_BREACH)
    ) {
      this.disabledReasons = [...this.disabledReasons, LicenseItemDisableReason.PREVENT_LICENSE_BREACH];
    } else if (
      this.currentAvailableNumberOfLicenses >= this.selectedCount &&
      this.disabledReasons.includes(LicenseItemDisableReason.PREVENT_LICENSE_BREACH)
    ) {
      this.disabledReasons = this.disabledReasons.filter((reason) => reason !== LicenseItemDisableReason.PREVENT_LICENSE_BREACH);
    }
  }

  private updateDisabledReasonsList(): void {
    this.disabledReasonsList = buildLicenseDisabledReasonsTooltip(
      this.disabledReasons,
      this.translationMapper,
      this.disabledReasonsMessage as TranslationKeys,
      this.translateService,
      this.loggerService,
    );
  }
}
